import { FeedParams, FeedResponse, Prompt, Course, Challenge } from '@ai-platform/utils';
import { api, ApiTags } from '../../api';
import { promptsActions } from '../../slices/prompts';
import { transformPromptDtoToModel } from '../prompt/mappers';
import { transformApiCoursesToFrontend, transformApiChallengesToFrontend } from './transformers';

const feedApi = api.injectEndpoints({
    endpoints: build => ({
        /**
         * Получить ленту с курсами, челленджами и промптами
         * GET /api/feed
         */
        getFeed: build.query<FeedResponse, FeedParams>({
            query: (params = {}) => ({
                url: '/feed',
                params: {
                    ...params,
                },
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                // Исключаем cursor из ключа кеша для работы merge функции
                const { cursor, limit, instanceId, ...stableArgs } = queryArgs;

                return stableArgs;
            },
            providesTags: result => {
                const tags: Array<{ type: keyof typeof ApiTags; id: string | number }> = [];

                if (result) {
                    // Добавляем теги для промптов
                    result.prompts.forEach((prompt: Prompt) => {
                        tags.push({ type: ApiTags.Prompts, id: prompt.id });
                    });

                    // Добавляем теги для курсов
                    result.courses.forEach((course: Course) => {
                        tags.push({ type: ApiTags.Courses, id: course.id });
                    });

                    // Добавляем теги для челленджей
                    result.challenges.forEach((challenge: Challenge) => {
                        tags.push({ type: ApiTags.Challenges, id: challenge.id });
                    });
                }

                return tags;
            },
            merge: (currentCache, newItems) => {
                // Дедупликация промптов
                const existingPromptIds = new Set(currentCache.prompts.map(p => p.id));
                const newPrompts = newItems.prompts.filter(p => !existingPromptIds.has(p.id));
                currentCache.prompts.push(...newPrompts);

                currentCache.next_cursor = newItems.next_cursor;
                currentCache.has_more_prompts = newItems.has_more_prompts;
                currentCache.has_more_courses = newItems.has_more_courses;
                currentCache.has_more_challenges = newItems.has_more_challenges;
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.cursor !== previousArg?.cursor;
            },
            transformResponse: (response: FeedResponse) => {
                response.prompts = response.prompts.map(prompt => ({
                    ...transformPromptDtoToModel(prompt),
                    type: 'prompt',
                }));
                response.courses = response.courses.map(prompt => {
                    return transformApiCoursesToFrontend(prompt);
                });
                response.challenges = response.challenges.map(prompt => {
                    return transformApiChallengesToFrontend(prompt);
                });

                return response;
            },
            onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;

                    // Добавляем промпты в EntityAdapter кеш
                    if (data.prompts && data.prompts.length > 0) {
                        dispatch(promptsActions.addPrompts(data.prompts));

                        // Если это первая загрузка (нет cursor), устанавливаем feed список
                        if (!params.cursor) {
                            dispatch(
                                promptsActions.setFeedPrompts({
                                    prompts: data.prompts,
                                    nextCursor: data.next_cursor,
                                    hasMore: data.has_more_prompts,
                                }),
                            );
                        } else {
                            // Иначе добавляем к существующему списку
                            dispatch(
                                promptsActions.appendFeedPrompts({
                                    prompts: data.prompts,
                                    nextCursor: data.next_cursor,
                                    hasMore: data.has_more_prompts,
                                }),
                            );
                        }
                    }
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
            // Кешируем данные на 1 минут для лучшей производительности
            keepUnusedDataFor: 60,
        }),
    }),
});

export const { useGetFeedQuery } = feedApi;

export { feedApi };
